/**
 * landingDefault.js — the host's DEFAULT landing page for `landing_type:"json"`.
 *
 * A widget's landing page is EITHER:
 *   - `landing_type:"html"` → the bundle ships `landing.html`, rendered verbatim
 *     (like index.html), OR
 *   - `landing_type:"json"` → the bundle ships `landing.json`, a structured
 *     `{ "blocks": [ … ] }` document. The host (NOT the designer) renders it with
 *     a default page so a widget gets an on-brand landing for free. This module
 *     IS that default page for the web (playground + studio preview); the iPhone
 *     app implements the SAME block contract natively in Dart.
 *
 * ── landing.json block contract (single source of truth, web + app) ──────────
 *   { "blocks": [ <block>, … ] }   rendered top-to-bottom, switch on `type`:
 *     hero   { type, title, subtitle?, image? }
 *     text   { type, title?, body }            body may contain line breaks
 *     image  { type, src, caption? }
 *     list   { type, title?, items:[ string | {title,text,icon?} ] }
 *     stats  { type, items:[ {value,label} ] }
 *     cta    { type, label, action, href? }    emits postMessage on click
 *   Unknown block types are skipped (forward-compatible).
 *
 * Rendered on the design-token floor (mirror of widget-design-kit/tokens.css)
 * and theme-aware via `userData.appearance` ∈ {light,dark,auto}. The returned
 * string is a complete, self-contained HTML document safe to drop into a
 * sandboxed `<iframe srcDoc>` / blob URL — it inlines its own styles and reads
 * no host globals.
 *
 * NOTE: this is ONE clean default layout — a placeholder the design team will
 * take over. (An earlier 5-preset experiment was removed.)
 */

// Mirror of widget-design-kit/tokens.css (:root light + dark + device fallback),
// trimmed to what the landing layout uses. The HOST owns this default page, so
// inlining the floor here is correct — keep visually in sync with tokens.css.
const TOKENS_CSS = `
:root{color-scheme:light dark;
--bg:#F2F2F7;--surface:#FFFFFF;--surface-2:#F7F7FA;--surface-3:#ECECF1;
--text:#1C1C1E;--text-2:#4A4A4F;--text-3:#8E8E93;
--line:rgba(0,0,0,0.10);--line-soft:rgba(0,0,0,0.05);
--accent:#9A8E7A;--accent-ink:#FFFFFF;--accent-soft:rgba(154,142,122,0.14);--accent-line:rgba(154,142,122,0.40);
--r-sm:10px;--r:16px;--r-lg:24px;--r-pill:999px;
--shadow-1:0 1px 3px rgba(0,0,0,0.06),0 1px 2px rgba(0,0,0,0.04);--shadow-2:0 8px 24px rgba(0,0,0,0.08);
--t-xs:11px;--t-sm:13px;--t-md:15px;--t-lg:17px;--t-xl:20px;--t-2xl:26px;--t-3xl:34px;--t-display:44px;
--sp-1:4px;--sp-2:8px;--sp-3:12px;--sp-4:16px;--sp-5:24px;--sp-6:32px;
--font-body:-apple-system,BlinkMacSystemFont,"SF Pro Text",system-ui,sans-serif;--font-display:var(--font-body);
--ease:cubic-bezier(0.22,0.61,0.36,1);}
[data-theme="dark"]{--bg:#000000;--surface:#1C1C1E;--surface-2:#2C2C2E;--surface-3:#3A3A3C;
--text:#F5F5F7;--text-2:#C9C9CE;--text-3:#8E8E93;--line:rgba(255,255,255,0.12);--line-soft:rgba(255,255,255,0.06);
--accent-soft:rgba(154,142,122,0.22);--accent-line:rgba(154,142,122,0.50);
--shadow-1:0 1px 3px rgba(0,0,0,0.4);--shadow-2:0 8px 24px rgba(0,0,0,0.5);}
@media(prefers-color-scheme:dark){:root:not([data-theme]){--bg:#000000;--surface:#1C1C1E;--surface-2:#2C2C2E;--surface-3:#3A3A3C;
--text:#F5F5F7;--text-2:#C9C9CE;--text-3:#8E8E93;--line:rgba(255,255,255,0.12);--line-soft:rgba(255,255,255,0.06);
--accent-soft:rgba(154,142,122,0.22);--accent-line:rgba(154,142,122,0.50);
--shadow-1:0 1px 3px rgba(0,0,0,0.4);--shadow-2:0 8px 24px rgba(0,0,0,0.5);}}
*{box-sizing:border-box;margin:0;padding:0;}
html,body{background:var(--bg);color:var(--text-2);font-family:var(--font-body);font-size:var(--t-md);line-height:1.5;-webkit-font-smoothing:antialiased;}
::-webkit-scrollbar{display:none;}html{scrollbar-width:none;}
`;

const LAYOUT_CSS = `
.lz-wrap{padding:var(--sp-5) var(--sp-4) calc(var(--sp-6) + 12px);display:flex;flex-direction:column;gap:var(--sp-5);}
.lz-hero-title{font-family:var(--font-display);font-weight:800;font-size:var(--t-3xl);line-height:1.1;color:var(--text);letter-spacing:-0.02em;}
.lz-hero-sub{font-size:var(--t-lg);color:var(--text-2);margin-top:var(--sp-2);}
.lz-hero-img{width:100%;aspect-ratio:16/9;object-fit:cover;border-radius:var(--r-lg);margin-top:var(--sp-4);box-shadow:var(--shadow-2);background:var(--surface-3);}
.lz-h{font-size:var(--t-lg);font-weight:700;color:var(--text);margin-bottom:var(--sp-2);}
.lz-body{font-size:var(--t-md);color:var(--text-2);white-space:pre-wrap;}
.lz-img{width:100%;object-fit:cover;border-radius:var(--r);box-shadow:var(--shadow-1);background:var(--surface-3);}
.lz-cap{font-size:var(--t-sm);color:var(--text-3);text-align:center;margin-top:var(--sp-2);}
.lz-list{display:flex;flex-direction:column;gap:var(--sp-2);}
.lz-li{display:flex;gap:var(--sp-3);align-items:flex-start;background:var(--surface);border:1px solid var(--line);border-radius:var(--r);padding:var(--sp-3) var(--sp-4);box-shadow:var(--shadow-1);}
.lz-li-bullet{display:flex;gap:var(--sp-3);align-items:flex-start;color:var(--text-2);font-size:var(--t-md);}
.lz-li-dot{color:var(--accent);font-size:var(--t-lg);line-height:1.3;flex:0 0 auto;}
.lz-li-ic{flex:0 0 auto;font-size:var(--t-xl);line-height:1.2;}
.lz-li-t{font-weight:650;color:var(--text);font-size:var(--t-md);}
.lz-li-x{color:var(--text-2);font-size:var(--t-sm);margin-top:2px;}
.lz-stats{display:flex;gap:var(--sp-2);background:var(--surface);border:1px solid var(--line);border-radius:var(--r-lg);padding:var(--sp-4);box-shadow:var(--shadow-1);}
.lz-stat{flex:1;text-align:center;display:flex;flex-direction:column;gap:var(--sp-1);}
.lz-stat + .lz-stat{border-left:1px solid var(--line-soft);}
.lz-stat-v{font-family:var(--font-display);font-weight:800;font-size:var(--t-2xl);color:var(--accent);line-height:1;}
.lz-stat-l{font-size:var(--t-xs);text-transform:uppercase;letter-spacing:0.06em;color:var(--text-3);}
.lz-cta{display:block;width:100%;border:none;cursor:pointer;background:var(--accent);color:var(--accent-ink);
font-family:var(--font-body);font-weight:700;font-size:var(--t-lg);padding:var(--sp-4);border-radius:var(--r-pill);
box-shadow:var(--shadow-2);transition:transform .12s var(--ease),opacity .12s var(--ease);}
.lz-cta:active{transform:scale(0.98);opacity:0.92;}
`;

// A generic sample landing, used by the studio to render the Landing preview
// "with example data" when a widget hasn't authored its own landing.json yet.
export const EXAMPLE_LANDING = {
  blocks: [
    { type: 'hero', title: 'Your Widget', subtitle: 'A one-line promise of what it does.', image: 'https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=800&q=80' },
    { type: 'text', title: 'What it does', body: 'A short paragraph describing the value.\nKeep it human and concrete — what the user gets.' },
    { type: 'stats', items: [{ value: '128', label: 'logged' }, { value: '4.9', label: 'rating' }, { value: '12', label: 'this week' }] },
    { type: 'list', title: 'Highlights', items: [{ icon: '⚡', title: 'Fast', text: 'One tap to log.' }, { icon: '🎯', title: 'Focused', text: 'Just what matters.' }, { icon: '📈', title: 'Insightful', text: 'See your trends.' }] },
    { type: 'image', src: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&q=80', caption: 'A glimpse of the widget in action.' },
    { type: 'cta', label: 'Use this widget', action: 'use' },
  ],
};

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderBlock(blk) {
  if (!blk || typeof blk !== 'object' || typeof blk.type !== 'string') return '';
  switch (blk.type) {
    case 'hero':
      return `<section class="lz-hero">`
        + (blk.title ? `<div class="lz-hero-title">${esc(blk.title)}</div>` : '')
        + (blk.subtitle ? `<div class="lz-hero-sub">${esc(blk.subtitle)}</div>` : '')
        + (blk.image ? `<img class="lz-hero-img" src="${esc(blk.image)}" alt="">` : '')
        + `</section>`;
    case 'text':
      return `<section class="lz-text">`
        + (blk.title ? `<div class="lz-h">${esc(blk.title)}</div>` : '')
        + `<div class="lz-body">${esc(blk.body)}</div>`
        + `</section>`;
    case 'image':
      return `<figure class="lz-image">`
        + (blk.src ? `<img class="lz-img" src="${esc(blk.src)}" alt="">` : '')
        + (blk.caption ? `<figcaption class="lz-cap">${esc(blk.caption)}</figcaption>` : '')
        + `</figure>`;
    case 'list': {
      const items = Array.isArray(blk.items) ? blk.items : [];
      const rows = items.map((it) => {
        if (it && typeof it === 'object') {
          return `<div class="lz-li">`
            + (it.icon ? `<div class="lz-li-ic">${esc(it.icon)}</div>` : '')
            + `<div><div class="lz-li-t">${esc(it.title)}</div>`
            + (it.text ? `<div class="lz-li-x">${esc(it.text)}</div>` : '')
            + `</div></div>`;
        }
        return `<div class="lz-li-bullet"><span class="lz-li-dot">•</span><span>${esc(it)}</span></div>`;
      }).join('');
      return `<section class="lz-list-wrap">`
        + (blk.title ? `<div class="lz-h">${esc(blk.title)}</div>` : '')
        + `<div class="lz-list">${rows}</div></section>`;
    }
    case 'stats': {
      const items = Array.isArray(blk.items) ? blk.items : [];
      const cells = items.map((s) =>
        `<div class="lz-stat"><div class="lz-stat-v">${esc(s && s.value)}</div>`
        + `<div class="lz-stat-l">${esc(s && s.label)}</div></div>`).join('');
      return `<section class="lz-stats">${cells}</section>`;
    }
    case 'cta':
      return `<button class="lz-cta" data-action="${esc(blk.action || 'use')}" data-href="${esc(blk.href || '')}">`
        + `${esc(blk.label || 'Use this widget')}</button>`;
    default:
      return ''; // unknown block — skipped (forward-compatible)
  }
}

/** Resolve the <html data-theme> attribute from appearance. "auto" → unset
 *  (let the device @media fallback decide); explicit light/dark → pin it. */
function themeAttr(appearance) {
  if (appearance === 'light' || appearance === 'dark') return ` data-theme="${appearance}"`;
  return '';
}

/**
 * Build the default landing HTML for a `landing_type:"json"` widget.
 * One clean default layout (a placeholder the design team will take over).
 * @param {object} landing  parsed landing.json (expects { blocks: [...] })
 * @param {object} [userData]  host user metadata; reads `.appearance`
 * @returns {string} a complete standalone HTML document
 */
export function buildDefaultLandingHtml(landing, userData) {
  const appearance = userData && userData.appearance;
  const blocks = landing && Array.isArray(landing.blocks) ? landing.blocks : null;
  const body = blocks && blocks.length
    ? `<div class="lz-wrap">${blocks.map(renderBlock).join('')}</div>`
    : `<div class="lz-wrap"><section class="lz-text"><div class="lz-h">No landing content</div>`
      + `<div class="lz-body">landing.json has no "blocks" to render.</div></section></div>`;
  // CTA clicks bubble to the host via postMessage so the eventual placement
  // (catalog/use-page, or the app) can act on them; harmless in the studio preview.
  const ctaScript = `<script>document.addEventListener('click',function(e){`
    + `var b=e.target.closest&&e.target.closest('.lz-cta');if(!b)return;`
    + `try{parent.postMessage({type:'landing_cta',action:b.getAttribute('data-action'),`
    + `href:b.getAttribute('data-href')||null},'*');}catch(_){}}); </script>`;
  return `<!doctype html><html${themeAttr(appearance)}><head><meta charset="utf-8">`
    + `<meta name="viewport" content="width=device-width,initial-scale=1">`
    + `<style>${TOKENS_CSS}${LAYOUT_CSS}</style></head>`
    + `<body>${body}${ctaScript}</body></html>`;
}

/** Studio empty-state when a widget declares no landing yet. */
export function landingEmptyStateHtml(appearance) {
  return `<!doctype html><html${themeAttr(appearance)}><head><meta charset="utf-8">`
    + `<style>${TOKENS_CSS}.lz-empty{height:100vh;display:flex;align-items:center;justify-content:center;`
    + `text-align:center;padding:var(--sp-6);color:var(--text-3);font-size:var(--t-sm);line-height:1.6;}</style>`
    + `</head><body><div class="lz-empty">No landing page yet.<br>Set <b>landing_type</b> to "html" or "json" `
    + `in widget.json and ship landing.html / landing.json.</div></body></html>`;
}

export const LANDING_BLOCK_TYPES = ['hero', 'text', 'image', 'list', 'stats', 'cta'];

// Landing presets for the studio switch. ONE default for now (a placeholder);
// the design team adds more later (extend this list + switch buildDefaultLandingHtml
// on the id, mirroring coverDefault's COVER_PRESETS).
export const LANDING_PRESETS = [
  { id: 'default', label: 'Default' },
];
